import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import DataTable from './Common/DataTable';
import Modal from './Common/Modal';
import { useAuth } from '../contexts/AuthContext';

export default function CampusManagement() {
  const { user } = useAuth();
  const isPrincipal = user?.role === 'principal';

  const [campuses, setCampuses] = useState([]);
  const [principals, setPrincipals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', address: '', principal_id: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await api.get('/campuses/');
      setCampuses(res.data || []);
    } catch (err) {
      console.error('Campus fetch error:', err);
      setError('Failed to load campuses');
    }

    try {
      const res = await api.get('/users/?role=principal');
      setPrincipals(res.data || []);
    } catch (err) {
      console.error('Principal fetch error:', err);
    }

    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/campuses/${editing.id}`, form);
      } else {
        await api.post('/campuses/', form);
      }
      setModalOpen(false);
      setEditing(null);
      setForm({ name: '', address: '', principal_id: '' });
      fetchData();
    } catch (err) {
      alert('Error saving campus: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this campus?')) return;
    try {
      await api.delete(`/campuses/${id}`);
      fetchData();
    } catch (err) {
      alert('Error deleting campus');
    }
  };

  const getPrincipalName = (id) => {
    const p = principals.find(x => x.id === id);
    return p ? `${p.first_name} ${p.last_name}` : 'Not Assigned';
  };

  // Principal read-only view
  if (isPrincipal) {
    return (
      <div className="bg-white p-6 rounded shadow">
        <h2 className="text-xl font-bold mb-4">🏫 My Campus</h2>
        {loading && <p>Loading...</p>}
        {!loading && campuses.length === 0 && (
          <p className="text-gray-500">No campus assigned.</p>
        )}
        {!loading && campuses.map(c => (
          <div key={c.id} className="space-y-2 border-b pb-4 mb-4">
            <p><strong>Name:</strong> {c.name}</p>
            <p><strong>Address:</strong> {c.address || '—'}</p>
            <p><strong>Phone:</strong> {c.phone || '—'}</p>
            <p className="text-xs text-gray-500">
              <i className="fas fa-lock"></i> Contact admin to modify
            </p>
          </div>
        ))}
      </div>
    );
  }

  const columns = ['Name', 'Address', 'Principal', 'Status', 'Actions'];
  const data = campuses.map(c => ({
    Name: c.name,
    Address: c.address || '—',
    Principal: getPrincipalName(c.principal_id),
    Status: (
      <span className={`px-2 py-1 rounded text-xs ${c.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {c.is_active ? 'Active' : 'Inactive'}
      </span>
    ),
    Actions: (
      <div className="flex gap-2">
        <button
          onClick={() => {
            setEditing(c);
            setForm({ name: c.name, address: c.address || '', principal_id: c.principal_id || '' });
            setModalOpen(true);
          }}
          className="text-blue-600 hover:text-blue-800"
        >
          <i className="fas fa-edit"></i>
        </button>
        <button
          onClick={() => handleDelete(c.id)}
          className="text-red-600 hover:text-red-800"
        >
          <i className="fas fa-trash"></i>
        </button>
      </div>
    ),
  }));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">🏫 Campus Management</h2>
        <button
          onClick={() => {
            setEditing(null);
            setForm({ name: '', address: '', principal_id: '' });
            setModalOpen(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          <i className="fas fa-plus mr-2"></i> Add Campus
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4">
          ❌ {error}
        </div>
      )}

      {loading ? (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">Loading...</div>
      ) : campuses.length === 0 ? (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          No campuses yet. Click "Add Campus" to create one.
        </div>
      ) : (
        <div className="bg-white p-4 rounded shadow">
          <DataTable columns={columns} data={data} />
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Campus' : 'Add Campus'}
        onSave={handleSubmit}
        saveLabel={editing ? 'Update' : 'Create'}
      >
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            className="w-full p-2 border rounded"
            placeholder="Campus Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <input
            className="w-full p-2 border rounded"
            placeholder="Address"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign Principal
            </label>
            <select
              className="w-full p-2 border rounded"
              value={form.principal_id}
              onChange={(e) => setForm({ ...form, principal_id: e.target.value })}
            >
              <option value="">— Select a principal —</option>
              {principals.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.first_name} {p.last_name} ({p.email})
                </option>
              ))}
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
}